import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import Marquee from "@/components/Marquee";
import { COMPANY } from "@/config/site";
import { usePageMeta } from "@/lib/meta";

const About = () => {
  usePageMeta(
    "About Us | CoachER",
    "CoachER is built for coaches, by coaches — an Ohio-based team creating the digital dugout assistant we wanted on our own teams.",
    "/about"
  );

  return (
    <div data-testid="about-page">
      <section className="relative overflow-hidden bg-coacher-navy pt-32 pb-20 sm:pt-44 sm:pb-28">
        <div className="absolute inset-0 pinstripes-dark" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-white/70">
            <span className="inline-block h-px w-10 bg-coacher-red" aria-hidden="true" />
            About Us
          </p>
          <MaskedLines
            lines={["Built for coaches,", "by coaches."]}
            delay={0.15}
            className="mt-6 max-w-4xl font-display text-4xl sm:text-6xl lg:text-7xl font-semibold uppercase leading-[1.0] tracking-tight text-white"
            lineClassName="[&:nth-child(2)]:text-outline-white"
          />
        </div>
      </section>

      <Marquee items={["Built from the dugout", "Ohio, USA", "Technology should support coaches"]} />

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-8">
          <Reveal>
            <p className="text-xl sm:text-2xl font-medium leading-relaxed text-coacher-navy">
              CoachER was created from a simple idea: youth coaches deserve better tools.
            </p>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-coacher-slate">
              Based in Ohio, USA, CoachER was built by people who understand what it's like to coach while balancing work, family, practices, games, schedules, lineups, communication, and everything else that comes with youth sports.
            </p>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-coacher-slate">
              We've been in the dugout. We've built practice plans late at night. We've adjusted batting orders minutes before first pitch. We've tried to keep track of player development while managing a dozen other things at once.
            </p>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-coacher-slate">
              That experience is what shaped CoachER.
            </p>
          </Reveal>

          <Reveal className="mt-14 border-l-4 border-coacher-red bg-coacher-surface p-8 sm:p-10">
            <p className="font-display text-xl sm:text-2xl font-semibold uppercase tracking-wide text-coacher-navy">
              Every feature starts with a simple question:
            </p>
            <p className="mt-3 text-lg sm:text-xl leading-relaxed text-coacher-slate italic">
              "Will this make coaching easier, more organized, or more effective?"
            </p>
          </Reveal>

          <Reveal className="mt-14">
            <p className="text-base sm:text-lg leading-relaxed text-coacher-slate">
              Rather than building another complicated sports platform, we set out to create something practical—an everyday coaching resource designed around the way youth baseball and softball actually operate.
            </p>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-coacher-slate">
              From practice planning and lineup management to batting orders, player development, and team organization, CoachER is designed to reduce the administrative load that comes with coaching so volunteers can spend more of their time focused on their players.
            </p>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-coacher-slate">
              We're especially proud to build CoachER in Ohio, where youth sports are deeply rooted in local communities and where countless seasons are made possible by parents and volunteers who step forward to coach.
            </p>
          </Reveal>

          <Reveal className="mt-16 grid gap-6 sm:grid-cols-2">
            <div data-testid="about-ohio-card" className="rounded-xl border border-coacher-border bg-white p-8 shadow-card">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-coacher-navy">
                <MapPin size={20} className="text-white" />
              </span>
              <h3 className="mt-5 font-display text-xl font-semibold uppercase tracking-wide text-coacher-navy">Ohio, USA</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-coacher-slate">
                Built where youth sports are woven into the community — and where every season runs on volunteers.
              </p>
            </div>
            <div data-testid="about-company-card" className="rounded-xl border border-coacher-border bg-white p-8 shadow-card">
              <span className="font-varsity text-4xl text-coacher-navy">BMW</span>
              <h3 className="mt-3 font-display text-xl font-semibold uppercase tracking-wide text-coacher-navy">{COMPANY.name}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-coacher-slate">
                CoachER is developed by {COMPANY.name}, an Ohio-based company focused on creating practical products that solve real problems.
              </p>
              <a
                href={COMPANY.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-company-link"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-coacher-navy underline decoration-coacher-red decoration-2 underline-offset-8 transition-colors hover:text-coacher-red"
              >
                Meet the team behind CoachER <ArrowUpRight size={16} />
              </a>
            </div>
          </Reveal>

          <Reveal className="mt-20 border-t border-coacher-border pt-14">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-coacher-red">Built from the dugout</p>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-coacher-slate">
              CoachER wasn't created by watching youth sports from the outside. It was created from inside the experience—planning practices, coaching players, managing teams, and finding better ways to make limited time count.
            </p>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-coacher-slate">
              We believe technology should support coaches, not complicate coaching. That's why we're building CoachER to be the digital dugout assistant we wanted on our own teams.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden bg-coacher-navy py-20 sm:py-24">
        <div className="absolute inset-0 pinstripes-dark" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-8">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-5xl font-semibold uppercase tracking-tight text-white">Come coach with us.</h2>
            <Link
              to="/pricing"
              data-testid="about-cta"
              className="mt-9 inline-flex items-center gap-2 rounded-md bg-coacher-red px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-coacher-red-dark"
            >
              Start Your Free Trial <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
